import {
  ROTULO_DA_MEDIDA,
  type MonitoramentoDeSaldos,
  type SaldoDaConta,
  type SalvarMinimosDeSaldo,
  type SituacaoDoSaldo,
} from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  CampoDeSelecao,
  CampoDeTexto,
  EstadoCarregando,
  EstadoDeErro,
  EstadoVazio,
  Etiqueta,
  Tabela,
  cn,
  toast,
  type ColunaDaTabela,
  type TomDaEtiqueta,
} from "@base/ui";
import { Bell, BellOff, LoaderCircle, RefreshCw, Search, Wallet, X } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link } from "../../../shared/navegacao/navegacao";
import { formatar } from "../../dashboard";
import {
  useChaveDeIa,
  useCredencialGoogle,
  useCredencialMeta,
  useDefinirMonitoramentoDaConta,
  useLerSaldosAgora,
  useMonitoramentoDeSaldos,
  useSalvarMinimosDeSaldo,
} from "../application/conexoes.contexto";
import { centavosDoTexto, reaisDosCentavos, textoDosCentavos } from "../domain/dinheiro";
import {
  FILTRO_INICIAL,
  filtrarSaldos,
  filtroAtivo,
  type FiltroDeSaldos,
  type FiltroDeSituacao,
} from "../domain/filtro-de-saldos";
import { CAMINHO_DAS_CONEXOES_DA_MONTE, CAMINHO_DO_MONITORAMENTO } from "./acesso-da-monte";

export { CAMINHO_DO_MONITORAMENTO };

const SITUACAO: Readonly<Record<SituacaoDoSaldo, { rotulo: string; tom: TomDaEtiqueta }>> = {
  "pagamento-pendente": { rotulo: "Pagamento pendente", tom: "perigo" },
  "abaixo-do-minimo": { rotulo: "Abaixo do mínimo", tom: "perigo" },
  "sem-saldo-medido": { rotulo: "Sem saldo medido", tom: "neutro" },
  "sem-minimo": { rotulo: "Sem mínimo", tom: "atencao" },
  ok: { rotulo: "Ok", tom: "sucesso" },
  inativa: { rotulo: "Inativa", tom: "contorno" },
  desativada: { rotulo: "Desativada", tom: "contorno" },
};

const PLATAFORMA = { meta: "Meta", google: "Google" } as const;
const chaveDa = (conta: Pick<SaldoDaConta, "plataforma" | "contaId">) => `${conta.plataforma}:${conta.contaId}`;

/**
 * Configuracoes da Monte > Monitoramento de Contas: os acessos da Monte ao Meta
 * e ao Google e o saldo de todas as contas que eles enxergam, com o minimo de
 * cada uma. So a equipe Monte chega aqui.
 */
export function PaginaDeMonitoramentoDeContas({
  nomeDoCliente,
}: {
  /** Nome da clinica pelo id, para dizer quem avisar. */
  readonly nomeDoCliente: (clienteId: string) => string | null;
}) {
  const meta = useCredencialMeta();
  const google = useCredencialGoogle();
  const ia = useChaveDeIa();
  const saldos = useMonitoramentoDeSaldos();
  const ler = useLerSaldosAgora();
  const lerDeNovo = () =>
    ler.mutate(undefined, { onError: (erro) => toast.error(mensagemDoErro(erro)) });

  return (
    <div className="flex flex-col gap-secao">
      <CabecalhoDePagina
        rotulo="Configurações da Monte"
        titulo="Monitoramento de Contas"
        descricao="Saldo das contas de anúncio da BM e da MCC da Monte, para nenhuma clínica parar de anunciar por falta de saldo."
      />

      <section className="flex flex-col gap-bloco" aria-labelledby="titulo-acessos">
        <div className="flex flex-col gap-1">
          <h2 id="titulo-acessos" className="text-xl font-normal tracking-tight">
            Acessos da Monte
          </h2>
        </div>
        {meta.isPending || google.isPending ? (
          <EstadoCarregando rotulo="Carregando os acessos" />
        ) : meta.isError || google.isError ? (
          <EstadoDeErro mensagem={mensagemDoErro(meta.error ?? google.error)} />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Meta {meta.data.configurada ? "conectado" : "sem token"} · Google{" "}
              {google.data.configurada ? "conectado" : "sem acesso"} · OpenAI{" "}
              {ia.data?.configurada ? "conectada" : "sem chave"}
            </p>
            <Button variant="outline" asChild>
              <Link para={CAMINHO_DAS_CONEXOES_DA_MONTE}>Gerenciar em Conexões</Link>
            </Button>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-bloco" aria-labelledby="titulo-saldos">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 id="titulo-saldos" className="text-xl font-normal tracking-tight">
              Saldo das contas
            </h2>
            <p className="text-sm text-muted-foreground">
              Lido de hora em hora.{" "}
              {saldos.data?.lidoEm ? `Última leitura em ${formatar.momento(saldos.data.lidoEm)}.` : "Ainda sem leitura."}
            </p>
          </div>
          <Button variant="outline" disabled={ler.isPending} onClick={lerDeNovo}>
            <RefreshCw className={cn(ler.isPending && "animate-spin")} aria-hidden="true" />
            {ler.isPending ? "Lendo saldos…" : "Atualizar agora"}
          </Button>
        </div>

        {saldos.isPending ? (
          <EstadoCarregando rotulo="Carregando os saldos" />
        ) : saldos.isError ? (
          <EstadoDeErro mensagem={mensagemDoErro(saldos.error)} />
        ) : (
          <PainelDeSaldos
            key={`${saldos.data.minimoPadraoCentavos}|${saldos.data.contas
              .filter((c) => c.minimoProprio)
              .map((c) => `${chaveDa(c)}=${c.minimoCentavos}`)
              .join(",")}`}
            dados={saldos.data}
            nomeDoCliente={nomeDoCliente}
            semAcesso={!meta.data?.configurada && !google.data?.configurada}
          />
        )}
      </section>
    </div>
  );
}

function PainelDeSaldos({
  dados,
  nomeDoCliente,
  semAcesso,
}: {
  readonly dados: MonitoramentoDeSaldos;
  readonly nomeDoCliente: (clienteId: string) => string | null;
  readonly semAcesso: boolean;
}) {
  const salvar = useSalvarMinimosDeSaldo();
  const definirMonitoramento = useDefinirMonitoramentoDaConta();
  const [padrao, setPadrao] = useState(() => textoDosCentavos(dados.minimoPadraoCentavos));
  const [proprios, setProprios] = useState<ReadonlyMap<string, string>>(
    () => new Map(dados.contas.filter((c) => c.minimoProprio).map((c) => [chaveDa(c), textoDosCentavos(c.minimoCentavos)])),
  );
  const [filtro, setFiltro] = useState<FiltroDeSaldos>(FILTRO_INICIAL);
  const filtrar = (mudanca: Partial<FiltroDeSaldos>) => setFiltro((atual) => ({ ...atual, ...mudanca }));

  const padraoCentavos = centavosDoTexto(padrao);
  const invalidas = [...proprios].filter(([, texto]) => centavosDoTexto(texto) === undefined).map(([chave]) => chave);
  const valido = padraoCentavos !== undefined && invalidas.length === 0;

  const inativas = dados.contas.filter((conta) => conta.situacao === "inativa").length;
  const visiveis = useMemo(
    () => filtrarSaldos(dados.contas, filtro, nomeDoCliente),
    [dados.contas, filtro, nomeDoCliente],
  );
  const clinicas = useMemo(() => {
    const ids = [...new Set(dados.contas.flatMap((conta) => conta.clientes))];
    return ids
      .map((id) => ({ valor: id, rotulo: nomeDoCliente(id) ?? "Clínica removida" }))
      .sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"));
  }, [dados.contas, nomeDoCliente]);
  const contagem = (situacao: SituacaoDoSaldo) => dados.contas.filter((conta) => conta.situacao === situacao).length;

  const corpo = (): SalvarMinimosDeSaldo => ({
    minimoPadraoCentavos: padraoCentavos ?? null,
    porConta: [...proprios].flatMap(([chave, texto]) => {
      const centavos = centavosDoTexto(texto);
      const conta = dados.contas.find((c) => chaveDa(c) === chave);
      return conta && typeof centavos === "number"
        ? [{ plataforma: conta.plataforma, contaId: conta.contaId, minimoCentavos: centavos }]
        : [];
    }),
  });
  const salvo = JSON.stringify({
    minimoPadraoCentavos: dados.minimoPadraoCentavos,
    porConta: dados.contas
      .filter((c) => c.minimoProprio)
      .map((c) => ({ plataforma: c.plataforma, contaId: c.contaId, minimoCentavos: c.minimoCentavos })),
  });
  const alterado = valido && JSON.stringify(corpo()) !== salvo;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!valido) return;
    salvar.mutate(corpo(), { onSuccess: () => toast.success("Saldos mínimos salvos") });
  }

  function editarMinimo(conta: SaldoDaConta, texto: string) {
    setProprios((atual) => {
      const proximo = new Map(atual);
      if (texto.trim() === "") proximo.delete(chaveDa(conta));
      else proximo.set(chaveDa(conta), texto);
      return proximo;
    });
  }

  const colunas: readonly ColunaDaTabela<SaldoDaConta>[] = [
    {
      chave: "conta",
      titulo: "Conta",
      celula: (conta) => (
        <div className="flex min-w-0 flex-col">
          <span className="font-medium">{conta.nome}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {PLATAFORMA[conta.plataforma]} · {conta.contaId}
          </span>
        </div>
      ),
    },
    {
      chave: "clinica",
      titulo: "Clínica",
      ocultarNoCelular: true,
      celula: (conta) =>
        conta.clientes.length === 0 ? (
          <span className="text-muted-foreground">Sem clínica</span>
        ) : (
          conta.clientes.map((id) => nomeDoCliente(id) ?? "Clínica removida").join(", ")
        ),
    },
    {
      chave: "saldo",
      titulo: "Saldo",
      numerica: true,
      celula: (conta) => (
        <div className="flex flex-col items-end">
          <span className="font-medium tabular-nums">{reaisDosCentavos(conta.saldoCentavos)}</span>
          <span className="text-xs text-muted-foreground" title={conta.observacao ?? undefined}>
            {ROTULO_DA_MEDIDA[conta.medida]}
          </span>
        </div>
      ),
    },
    {
      chave: "minimo",
      titulo: "Mínimo",
      numerica: true,
      celula: (conta) => {
        const chave = chaveDa(conta);
        const invalido = invalidas.includes(chave);
        return (
          <input
            aria-label={`Saldo mínimo de ${conta.nome}`}
            aria-invalid={invalido || undefined}
            inputMode="decimal"
            placeholder={padrao ? `${padrao} (padrão)` : "Sem padrão"}
            value={proprios.get(chave) ?? ""}
            onChange={(evento) => editarMinimo(conta, evento.target.value)}
            className={cn(
              "h-9 w-36 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums",
              "placeholder:text-muted-foreground/70 aria-invalid:border-destructive",
            )}
          />
        );
      },
    },
    {
      chave: "situacao",
      titulo: "Situação",
      celula: (conta) => <Etiqueta tom={SITUACAO[conta.situacao].tom}>{SITUACAO[conta.situacao].rotulo}</Etiqueta>,
    },
    {
      chave: "alerta",
      titulo: "Alerta",
      celula: (conta) => {
        const ocupado =
          definirMonitoramento.isPending &&
          definirMonitoramento.variables?.plataforma === conta.plataforma &&
          definirMonitoramento.variables.contaId === conta.contaId;
        return (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={ocupado}
            aria-pressed={conta.monitorada}
            aria-label={`${conta.monitorada ? "Desativar" : "Reativar"} o alerta de ${conta.nome}`}
            title={conta.monitorada ? "Desativar: a conta deixa de gerar alerta" : "Reativar o alerta desta conta"}
            onClick={() =>
              definirMonitoramento.mutate(
                { plataforma: conta.plataforma, contaId: conta.contaId, monitorada: !conta.monitorada },
                {
                  onSuccess: () =>
                    toast.success(conta.monitorada ? `Alerta desativado: ${conta.nome}` : `Alerta reativado: ${conta.nome}`),
                  onError: (erro) => toast.error(mensagemDoErro(erro)),
                },
              )
            }
            className={cn(!conta.monitorada && "text-muted-foreground")}
          >
            {ocupado ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : conta.monitorada ? (
              <Bell aria-hidden="true" />
            ) : (
              <BellOff aria-hidden="true" />
            )}
            {conta.monitorada ? "Ligado" : "Desligado"}
          </Button>
        );
      },
    },
  ];

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-bloco">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar pela situação">
        {(["pagamento-pendente", "abaixo-do-minimo", "sem-saldo-medido", "ok"] as const).map((situacao) => {
          const ativo = filtro.situacao === situacao;
          return (
            <button
              key={situacao}
              type="button"
              aria-pressed={ativo}
              onClick={() => filtrar({ situacao: ativo ? "todas" : situacao })}
              className={cn(
                "rounded-full ring-offset-2 ring-offset-background transition-shadow",
                ativo && "ring-2 ring-ring",
              )}
            >
              <Etiqueta tom={contagem(situacao) > 0 ? SITUACAO[situacao].tom : "neutro"}>
                {contagem(situacao)} {SITUACAO[situacao].rotulo.toLowerCase()}
              </Etiqueta>
            </button>
          );
        })}
      </div>

      {dados.falhas.map((falha) => (
        <p key={falha.plataforma} role="alert" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {PLATAFORMA[falha.plataforma]}: {falha.mensagem}
        </p>
      ))}

      <div className="flex flex-wrap items-end gap-4 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70">
        <div className="w-full max-w-xs">
          <CampoDeTexto
            rotulo="Saldo mínimo padrão (R$)"
            inputMode="decimal"
            placeholder="Ex.: 500,00"
            value={padrao}
            onChange={(evento) => setPadrao(evento.target.value)}
            erros={padraoCentavos === undefined ? ["Use só números, como 500 ou 1.250,00"] : undefined}
            dica="Vale para toda conta sem mínimo próprio."
          />
        </div>
        <p className="max-w-md flex-1 text-sm text-muted-foreground">
          Para uma conta que gasta mais, preencha o mínimo dela na tabela. Deixe em branco para usar o padrão.
        </p>
        <Button type="submit" disabled={!alterado || salvar.isPending}>
          {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
          Salvar mínimos
        </Button>
      </div>
      {invalidas.length > 0 ? (
        <p role="alert" className="text-sm text-destructive">
          Confira os mínimos marcados em vermelho: use só números, como 500 ou 1.250,00.
        </p>
      ) : null}
      {salvar.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDoErro(salvar.error)}
        </p>
      ) : null}

      {dados.contas.length === 0 ? (
        <EstadoVazio
          icone={<Wallet aria-hidden="true" />}
          titulo={semAcesso ? "Conecte o Meta ou o Google em Conexões" : "Nenhuma conta lida ainda"}
          descricao={
            semAcesso
              ? "Com um acesso da Monte salvo, as contas aparecem aqui com o saldo de cada uma."
              : "Clique em Atualizar agora para ler o saldo das contas pela primeira vez."
          }
        />
      ) : (
        <>
          <div className="flex flex-col gap-4 rounded-3xl bg-card p-4 shadow-sm ring-1 ring-border/70 md:p-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,1fr))]">
              <CampoDeTexto
                rotulo="Buscar"
                type="search"
                placeholder="Conta, ID ou clínica"
                icone={<Search aria-hidden="true" />}
                value={filtro.busca}
                onChange={(evento) => filtrar({ busca: evento.target.value })}
              />
              <CampoDeSelecao
                rotulo="Plataforma"
                value={filtro.plataforma}
                onChange={(evento) => filtrar({ plataforma: evento.target.value as FiltroDeSaldos["plataforma"] })}
                opcoes={[
                  { valor: "todas", rotulo: "Todas" },
                  { valor: "meta", rotulo: "Meta" },
                  { valor: "google", rotulo: "Google" },
                ]}
              />
              <CampoDeSelecao
                rotulo="Situação"
                value={filtro.situacao}
                onChange={(evento) => filtrar({ situacao: evento.target.value as FiltroDeSituacao })}
                opcoes={[
                  { valor: "todas", rotulo: "Todas" },
                  { valor: "precisa-de-acao", rotulo: "Precisa de ação" },
                  ...(Object.keys(SITUACAO) as SituacaoDoSaldo[]).map((situacao) => ({
                    valor: situacao,
                    rotulo: SITUACAO[situacao].rotulo,
                  })),
                ]}
              />
              <CampoDeSelecao
                rotulo="Clínica"
                value={filtro.clinica}
                onChange={(evento) => filtrar({ clinica: evento.target.value })}
                opcoes={[
                  { valor: "todas", rotulo: "Todas" },
                  { valor: "sem-clinica", rotulo: "Sem clínica" },
                  ...clinicas,
                ]}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <div className="flex flex-wrap items-center gap-4">
                <span aria-live="polite">
                  {visiveis.length} de {dados.contas.length} {dados.contas.length === 1 ? "conta" : "contas"}
                </span>
                {inativas > 0 && filtro.situacao !== "inativa" ? (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filtro.comInativas}
                      onChange={(evento) => filtrar({ comInativas: evento.target.checked })}
                      className="size-4 accent-[var(--color-primary)]"
                    />
                    Mostrar contas inativas ({inativas})
                  </label>
                ) : null}
              </div>
              {filtroAtivo(filtro) ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => setFiltro(FILTRO_INICIAL)}>
                  <X aria-hidden="true" />
                  Limpar filtros
                </Button>
              ) : null}
            </div>
          </div>
          {visiveis.length === 0 ? (
            <EstadoVazio
              icone={<Search aria-hidden="true" />}
              titulo="Nenhuma conta com esses filtros"
              descricao="Mude a busca ou os filtros para ver outras contas."
              acao={
                <Button type="button" variant="outline" onClick={() => setFiltro(FILTRO_INICIAL)}>
                  Limpar filtros
                </Button>
              }
            />
          ) : (
            <Tabela
              legenda="Saldo das contas de anúncio, das que pedem ação primeiro"
              colunas={colunas}
              itens={visiveis}
              chaveDe={chaveDa}
            />
          )}
        </>
      )}
    </form>
  );
}
